import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  ChefHat, 
  Star, 
  Heart,
  Award,
  ArrowRight,
  Users,
  Clock,
  MapPin,
  Utensils,
  Wine,
  Leaf,
  Globe
} from 'lucide-react';

const ChefServicePage = () => {
  const [selectedCuisine, setSelectedCuisine] = useState('all');

  const cuisineTypes = [
    { id: 'all', name: 'Toutes Cuisines', icon: Globe },
    { id: 'french', name: 'Française', icon: ChefHat },
    { id: 'italian', name: 'Italienne', icon: Heart },
    { id: 'asian', name: 'Asiatique', icon: Leaf },
    { id: 'mediterranean', name: 'Méditerranéenne', icon: Utensils }
  ];

  const chefs = [
    {
      id: 1,
      name: "Chef Antoine Dubois",
      specialty: "Cuisine Française Moderne",
      cuisine: "french",
      stars: "2 étoiles Michelin",
      experience: "15 ans",
      image: "https://images.unsplash.com/photo-1577219491135-ce391730fb2c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      price: "À partir de 1 200€/service",
      description: "Ancien chef du Bristol Paris, spécialiste de la haute gastronomie française avec une approche contemporaine.",
      signature: "Homard bleu aux légumes de saison",
      availability: "Paris, Côte d'Azur, Londres",
      services: ["Dîner gastronomique", "Brunch d'exception", "Cours de cuisine", "Menu dégustation"]
    },
    {
      id: 2,
      name: "Chef Isabella Romano",
      specialty: "Cuisine Italienne Authentique",
      cuisine: "italian",
      stars: "1 étoile Michelin",
      experience: "12 ans",
      image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      price: "À partir de 950€/service",
      description: "Formée dans les meilleures trattorias d'Italie, elle apporte l'authenticité toscane à votre villa.",
      signature: "Risotto aux truffes blanches d'Alba",
      availability: "Italie, Monaco, Saint-Tropez",
      services: ["Pasta fraîche artisanale", "Soirée pizza gourmet", "Dégustation vins italiens", "Antipasti créatifs"]
    },
    {
      id: 3,
      name: "Chef Kenji Yamamoto",
      specialty: "Fusion Nikkei",
      cuisine: "asian",
      stars: "Meilleur Chef Asie 2023",
      experience: "18 ans",
      image: "https://images.unsplash.com/photo-1583394838336-acd977736f90?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      price: "À partir de 1 500€/service",
      description: "Expert en fusion japonaise-péruvienne, créateur d'expériences culinaires uniques et spectaculaires.",
      signature: "Omakase dégustation 12 services",
      availability: "International",
      services: ["Omakase privé", "Sushi master class", "Tempura créatif", "Cérémonie du thé"]
    },
    {
      id: 4,
      name: "Chef Maria Gonzalez",
      specialty: "Méditerranéenne Moderne",
      cuisine: "mediterranean",
      stars: "Étoile verte Michelin",
      experience: "10 ans",
      image: "https://images.unsplash.com/photo-1574943320219-553eb213f72d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      price: "À partir de 800€/service",
      description: "Pionnière de la cuisine durable, elle sublime les produits locaux méditerranéens avec créativité.",
      signature: "Paella de fruits de mer aux algues",
      availability: "Espagne, Baléares, Costa Brava",
      services: ["Cuisine locale bio", "Barbecue premium", "Tapas créatives", "Paella spectacle"]
    }
  ];

  const serviceTypes = [
    {
      icon: Utensils,
      title: "Dîner Gastronomique",
      description: "Menu dégustation multi-services avec produits d'exception",
      duration: "3-4 heures",
      guests: "2-16 personnes",
      includes: ["Chef + assistant", "Tous ingrédients", "Service à table", "Nettoyage"]
    },
    {
      icon: Wine,
      title: "Expérience Œnologique",
      description: "Dîner avec accords mets-vins par un sommelier expert",
      duration: "4-5 heures",
      guests: "4-12 personnes",
      includes: ["Chef + sommelier", "Vins premium", "Dégustation guidée", "Présentation des plats"]
    },
    {
      icon: ChefHat,
      title: "Cours de Cuisine Privé",
      description: "Apprenez les secrets de la haute gastronomie",
      duration: "2-3 heures",
      guests: "2-8 personnes",
      includes: ["Chef instructeur", "Tabliers fournis", "Recettes exclusives", "Dégustation finale"]
    },
    {
      icon: Heart,
      title: "Brunch d'Exception",
      description: "Brunch gastronomique pour occasions spéciales",
      duration: "2-3 heures",
      guests: "4-20 personnes",
      includes: ["Buffet premium", "Service continu", "Boissons incluses", "Présentation élégante"]
    }
  ];

  const filteredChefs = selectedCuisine === 'all' 
    ? chefs 
    : chefs.filter(chef => chef.cuisine === selectedCuisine);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <Badge 
            variant="secondary" 
            className="mb-6 px-6 py-3 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-wider"
          >
            <ChefHat className="w-4 h-4 mr-2" />
            Gastronomie d'Exception
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 tracking-wide"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
          >
            Chef à Domicile
          </h1>
          
          <p 
            className="text-xl md:text-2xl font-light mb-12 opacity-90 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Les plus grands chefs étoilés se déplacent dans votre villa pour créer des expériences culinaires inoubliables
          </p>

          <Button 
            size="lg"
            className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
          >
            Réserver un Chef
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Cuisine Filter */}
      <section className="py-12 px-6 bg-white/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {cuisineTypes.map((cuisine) => {
              const IconComponent = cuisine.icon;
              return (
                <Button
                  key={cuisine.id}
                  variant={selectedCuisine === cuisine.id ? 'default' : 'outline'}
                  onClick={() => setSelectedCuisine(cuisine.id)}
                  className={`px-6 py-3 rounded-full transition-all duration-300 ${
                    selectedCuisine === cuisine.id
                      ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg transform -translate-y-1'
                      : 'bg-white/80 text-gray-700 border-gray-200 hover:bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <IconComponent className="w-4 h-4 mr-2" />
                  {cuisine.name}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Chefs Portfolio */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Nos Chefs d'Exception
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Une sélection exclusive des meilleurs chefs étoilés au monde
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {filteredChefs.map((chef) => (
              <Card key={chef.id} className="overflow-hidden hover:shadow-2xl transition-all duration-500 group">
                <div className="relative h-80">
                  <img 
                    src={chef.image}
                    alt={chef.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-white/20 backdrop-blur-md border-white/30 text-white">
                      <Award className="w-3 h-3 mr-1" />
                      {chef.stars}
                    </Badge>
                  </div>

                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <h3 
                      className="text-2xl font-light mb-2"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {chef.name}
                    </h3>
                    <p className="text-sm opacity-90 mb-2">{chef.specialty}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span>{chef.experience} d'expérience</span>
                      <span className="text-lg font-light">{chef.price}</span>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="mb-6">
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {chef.description}
                    </p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-orange-600" />
                        <span className="font-medium">Signature:</span>
                        <span className="text-gray-600">{chef.signature}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-orange-600" />
                        <span className="font-medium">Disponible:</span>
                        <span className="text-gray-600">{chef.availability}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-6">
                    <h4 className="font-medium text-gray-800 text-sm">Services proposés :</h4>
                    {chef.services.map((service, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-orange-600 rounded-full" />
                        <span className="text-xs text-gray-600">{service}</span>
                      </div>
                    ))}
                  </div>

                  <Button 
                    className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white transition-all duration-300"
                  >
                    Réserver {chef.name.split(' ')[1]}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Types */}
      <section className="py-20 px-6 bg-gradient-to-r from-orange-900 to-red-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Types de Services
            </h2>
            <p 
              className="text-xl opacity-90 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Des expériences culinaires adaptées à chaque occasion
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {serviceTypes.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="p-6 bg-white/10 backdrop-blur-md border-white/20 text-white">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-orange-300" />
                    </div>
                    <div>
                      <h3 className="text-xl font-light mb-2">{service.title}</h3>
                      <p className="text-orange-200 text-sm leading-relaxed">{service.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-xs text-orange-100 mb-4">
                    <div>
                      <Clock className="w-3 h-3 inline mr-1" />
                      {service.duration}
                    </div>
                    <div>
                      <Users className="w-3 h-3 inline mr-1" />
                      {service.guests}
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-xs text-orange-300 font-medium">Inclus :</p>
                    {service.includes.map((item, idx) => (
                      <div key={idx} className="text-xs text-orange-200">
                        • {item}
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Booking Process */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Comment ça Marche
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              De la sélection du chef à la dégustation finale
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Sélection", desc: "Choisissez votre chef selon vos goûts culinaires" },
              { step: "2", title: "Menu", desc: "Conception d'un menu personnalisé avec le chef" },
              { step: "3", title: "Préparatifs", desc: "Le chef s'occupe de tous les approvisionnements" },
              { step: "4", title: "Dégustation", desc: "Savourez votre expérience gastronomique unique" }
            ].map((phase, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-light text-white">{phase.step}</span>
                </div>
                <h3 className="text-xl font-light text-gray-800 mb-4">{phase.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Une Expérience Gastronomique Unique
          </h2>
          <p 
            className="text-xl mb-8 text-gray-600 leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Transformez votre villa en restaurant étoilé le temps d'une soirée mémorable
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
            >
              Réserver un Chef
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300"
            >
              Voir tous les Chefs
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600">
            <div className="flex items-center justify-center gap-2">
              <Award className="w-4 h-4 text-orange-600" />
              <span>Chefs étoilés exclusivement</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Clock className="w-4 h-4 text-orange-600" />
              <span>Réservation 48h à l'avance</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Heart className="w-4 h-4 text-orange-600" />
              <span>Menu 100% personnalisé</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChefServicePage;